import { motion } from "framer-motion";
import { FolderOpen } from "lucide-react";
import { springs } from "@/components/ui";
import { DocumentGridCard } from "./DocumentGridCard";
import { getCategoryLucideIcon } from "./category-icon-map";
import { useDocumentCategoryLabel } from "./use-document-category-label";
import type { DocumentView } from "./types";

interface DocumentGridViewProps {
  groupedDocuments?: Map<string, DocumentView[]> | null;
  documents?: DocumentView[];
  onOpen: (id: string) => void;
  onTagClick?: (docId: string) => void;
}

function CardGrid({
  docs,
  indexOffset = 0,
  onOpen,
  onTagClick,
}: {
  docs: DocumentView[];
  indexOffset?: number;
  onOpen: (id: string) => void;
  onTagClick?: (docId: string) => void;
}) {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-card-gap">
      {docs.map((doc, i) => (
        <DocumentGridCard
          key={doc.id}
          doc={doc}
          index={indexOffset + i}
          onOpen={onOpen}
          onTagClick={onTagClick}
        />
      ))}
    </div>
  );
}

export function DocumentGridView({
  groupedDocuments,
  documents,
  onOpen,
  onTagClick,
}: DocumentGridViewProps) {
  const getCategoryLabel = useDocumentCategoryLabel();
  const isGroupedMode = groupedDocuments != null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={springs.snappy}
      className="h-full overflow-auto p-1 slim-scroll"
    >
      {isGroupedMode ? (
        (() => {
          let indexOffset = 0;
          return Array.from(groupedDocuments.entries()).map(
            ([categoryKey, docs]) => {
              const firstDoc = docs[0];
              const iconName = firstDoc?.categoryIcon ?? "folder";
              const groupTitle = firstDoc?.categoryId
                ? getCategoryLabel({
                    name: firstDoc.categoryName ?? "",
                    slug: firstDoc.categorySlug,
                  })
                : getCategoryLabel({
                    name: "Uncategorized",
                    slug: "uncategorized",
                  });
              const IconComponent =
                getCategoryLucideIcon(iconName) ?? FolderOpen;
              const currentOffset = indexOffset;
              indexOffset += docs.length;

              return (
                <div key={categoryKey} className="mb-6 last:mb-0">
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <IconComponent className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-foreground">
                      {groupTitle}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {docs.length}
                    </span>
                  </div>
                  <CardGrid
                    docs={docs}
                    indexOffset={currentOffset}
                    onOpen={onOpen}
                    onTagClick={onTagClick}
                  />
                </div>
              );
            },
          );
        })()
      ) : (
        <CardGrid
          docs={documents ?? []}
          onOpen={onOpen}
          onTagClick={onTagClick}
        />
      )}
    </motion.div>
  );
}
